/** Diálogo de edición del presupuesto de un cliente: monto total + segmentos. */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { OutletPicker } from "@/components/feature/media/outlet-picker";
import { useOutletKinds } from "@/hooks/use-outlet-kinds";
import {
  media, type ClientBudgetOut, type ClientBudgetSegment,
} from "@/lib/api";

export function BudgetDialog({
  clientId, open, onOpenChange, budget,
}: {
  clientId: number;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  budget: ClientBudgetOut | null;
}) {
  const qc = useQueryClient();
  const { kinds } = useOutletKinds();
  const [total, setTotal] = useState("");
  const [notes, setNotes] = useState("");
  const [segments, setSegments] = useState<ClientBudgetSegment[]>([]);
  const firstKind = kinds[0]?.code ?? null;

  useEffect(() => {
    if (!open) return;
    setTotal(budget?.total_amount != null ? String(budget.total_amount) : "");
    setNotes(budget?.notes ?? "");
    setSegments(budget?.segments?.map(s => ({ ...s })) ?? []);
  }, [open, budget]);

  const save = useMutation({
    mutationFn: () => media.putClientBudget(clientId, {
      total_amount: total.trim() === "" ? null : Number(total),
      notes: notes || null,
      segments: segments.map(s => ({
        scope: s.scope,
        outlet_kind: s.scope === "media_kind" ? s.outlet_kind : null,
        outlet_id: s.scope === "outlet" ? s.outlet_id : null,
        amount: Number(s.amount) || 0,
      })),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "budget", clientId] });
      qc.invalidateQueries({ queryKey: ["media", "budget", "consumption", clientId] });
      toast.success("Presupuesto guardado");
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function addSegment() {
    setSegments(prev => [...prev, { scope: "media_kind", outlet_kind: firstKind, outlet_id: null, amount: 0 }]);
  }
  function updateSegment(i: number, patch: Partial<ClientBudgetSegment>) {
    setSegments(prev => prev.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  }
  function removeSegment(i: number) {
    setSegments(prev => prev.filter((_, idx) => idx !== i));
  }

  function submit() {
    for (const s of segments) {
      if (s.scope === "media_kind" && !s.outlet_kind) { toast.error("Falta tipo de medio en un segmento"); return; }
      if (s.scope === "outlet" && !s.outlet_id) { toast.error("Falta el medio en un segmento"); return; }
      if (!(Number(s.amount) > 0)) { toast.error("Monto de segmento inválido"); return; }
    }
    save.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Presupuesto del cliente</DialogTitle>
          <DialogDescription>
            Monto total y asignaciones por tipo de medio o medio puntual. El consumo se calcula
            de los costos de los avisos (excluye descartados y cancelados).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Presupuesto total</Label>
              <Input type="number" min={0} step="0.01" value={total}
                onChange={(e) => setTotal(e.target.value)} placeholder="50000.00" className="font-mono" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notas</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <div className="flex items-center justify-between pt-1">
            <Label className="text-xs">Segmentos ({segments.length})</Label>
            <Button type="button" size="sm" variant="outline" onClick={addSegment}>
              <Plus size={14} /> Agregar segmento
            </Button>
          </div>

          {segments.map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end border border-ink-200 dark:border-ink-700 rounded-lg p-2">
              <div className="col-span-4">
                <Label className="text-[10px]">Alcance</Label>
                <Select value={s.scope}
                  onValueChange={(v) => updateSegment(i, {
                    scope: v as ClientBudgetSegment["scope"],
                    outlet_kind: v === "media_kind" ? (s.outlet_kind ?? firstKind) : null,
                    outlet_id: v === "outlet" ? s.outlet_id : null,
                  })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="media_kind">Tipo de medio</SelectItem>
                    <SelectItem value="outlet">Medio puntual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-5">
                {s.scope === "media_kind" ? (
                  <>
                    <Label className="text-[10px]">Tipo de medio</Label>
                    <Select value={s.outlet_kind ?? ""}
                      onValueChange={(v) => updateSegment(i, { outlet_kind: v })}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        {kinds.map(k => (
                          <SelectItem key={k.code} value={k.code}>{k.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                ) : (
                  <OutletPicker
                    label="Medio"
                    value={s.outlet_id}
                    onChange={(oid) => updateSegment(i, { outlet_id: oid })}
                  />
                )}
              </div>
              <div className="col-span-2">
                <Label className="text-[10px]">Monto</Label>
                <Input type="number" min={0} step="0.01" value={s.amount}
                  onChange={(e) => updateSegment(i, { amount: Number(e.target.value) })}
                  className="font-mono" />
              </div>
              <div className="col-span-1">
                <Button type="button" size="icon" variant="ghost" className="h-9 w-9 text-bad"
                  onClick={() => removeSegment(i)} title="Quitar">
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={save.isPending}>
            {save.isPending ? "Guardando…" : "Guardar presupuesto"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
